import { useCallback, useEffect, useRef, useState } from 'react';
import { uid } from '../utils/format';

export interface PendingFile {
  id: string;
  name: string;
  size: number;
  progress: number;
  kind: 'pdf' | 'image';
}

export function useUploader() {
  const [files, setFiles] = useState<PendingFile[]>([]);
  const timers = useRef<number[]>([]);

  useEffect(() => () => timers.current.forEach((t) => window.clearInterval(t)), []);

  const simulate = useCallback((id: string) => {
    const t = window.setInterval(() => {
      setFiles((prev) =>
        prev.map((f) => {
          if (f.id !== id) return f;
          const next = Math.min(100, f.progress + 8 + Math.random() * 18);
          if (next >= 100) window.clearInterval(t);
          return { ...f, progress: next };
        }),
      );
    }, 120);
    timers.current.push(t);
  }, []);

  const addFiles = useCallback(
    (list: FileList | File[] | null) => {
      if (!list) return;
      const added = Array.from(list).map<PendingFile>((f) => ({
        id: uid('f'),
        name: f.name,
        size: f.size,
        progress: 0,
        kind: f.type.startsWith('image') ? 'image' : 'pdf',
      }));
      setFiles((prev) => [...prev, ...added]);
      added.forEach((f) => simulate(f.id));
    },
    [simulate],
  );

  const addSample = useCallback(
    (name: string, kind: PendingFile['kind'] = 'pdf') => {
      const f: PendingFile = { id: uid('f'), name, size: 380000 + Math.round(Math.random() * 900000), progress: 0, kind };
      setFiles((prev) => [...prev, f]);
      simulate(f.id);
    },
    [simulate],
  );

  const remove = useCallback((id: string) => setFiles((prev) => prev.filter((f) => f.id !== id)), []);
  const reset = useCallback(() => setFiles([]), []);

  const allDone = files.length > 0 && files.every((f) => f.progress >= 100);

  return { files, addFiles, addSample, remove, reset, allDone };
}
